import React, { useState } from "react";
import {
    Button,
    ExpansionPanel,
    ExpansionPanelDetails,
    ExpansionPanelSummary,
    IconButton,
    Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DevicesOtherOutlinedIcon from "@material-ui/icons/DevicesOtherOutlined";
import { useSnackbar } from "notistack";

import { AjaxResult } from "../../../../../enums/ajaxResult";
import { SiteCommissioningItem, SiteCommissioningSectionSummary, SiteCommissioningSummary } from "../../../../../dtos/siteCommissioning";
import { useApi } from "../../../../useApi";
import { useConfirmDialog } from "../../../../../hooks/useConfirmDialog";
import { useForceRerender } from "../../../../../hooks/useForceRerender";

import { DynamicFieldsList } from "./DynamicFieldsList";
import { NetworkInterfacesView } from "./NetworkInterfacesView";
import { ServicesView } from "./ServicesView";
import { SiteTopologyView } from "./SiteTopologyView";
import { SectionStatusChip } from "./SectionStatusChip";
import { SectionJsonView } from "./SectionJsonView";
import { formatRelativeTime } from "./formatRelativeTime";
import { EditHmiConfigurationDialog } from "./EditHmiConfigurationDialog";
import { EditFingerprintConfigurationDialog } from "./EditFingerprintConfigurationDialog";
import { EditSystemConfigurationDialog } from "./EditSystemConfigurationDialog";
import { EditBiometricDeviceDialog } from "./EditBiometricDeviceDialog";
import { ZoneHead } from "../console/ZoneHead";
import { tokens } from "../console/tokens";

const SECTION_LABELS: Record<string, string> = {
    hmiConfiguration: "HMI",
    systemConfiguration: "System Configuration",
    networkInterfaces: "Network Interfaces",
    services: "Services",
};

type EditableSingletonSection = "hmiConfiguration" | "fingerprintConfiguration" | "systemConfiguration";

type RowKind = "singleton" | "biometricDevices" | "boxes" | "ioDevices" | "networkInterfaces" | "services" | "generic";

interface DisplayRow {
    key: string;
    label: string;
    kind: RowKind;
    dataSection: SiteCommissioningSectionSummary;
    items: Record<string, SiteCommissioningItem>;
}

const STATUS_SEVERITY: Record<string, number> = { Failed: 3, Syncing: 2, Deferred: 2, Unreachable: 1 };

function worseStatus(a?: string | null, b?: string | null): string | null | undefined {
    if (!a) return b;
    if (!b) return a;
    if (a === b) return a;
    return (STATUS_SEVERITY[a] ?? 0) >= (STATUS_SEVERITY[b] ?? 0) ? a : b;
}

function latestTimestamp(a?: string | null, b?: string | null): string {
    if (!a) return b ?? "";
    if (!b) return a;
    return new Date(b).getTime() > new Date(a).getTime() ? b : a;
}

function humanizeSectionName(name: string): string {
    const spaced = name.replace(/([a-z0-9])([A-Z])/g, "$1 $2");
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function summarizeRow(kind: RowKind, items: Record<string, SiteCommissioningItem>): string {
    if (kind === "ioDevices") {
        const keys = Object.keys(items);
        const parts = [
            { label: "sensor", count: keys.filter((k) => k.startsWith("sensor:")).length },
            { label: "device", count: keys.filter((k) => k.startsWith("ioDevice:")).length },
        ].filter((p) => p.count > 0);
        return parts.map((p) => `${p.count} ${p.label}${p.count === 1 ? "" : "s"}`).join(" · ") || "Empty";
    }
    if (kind === "boxes") {
        const count = Object.keys(items).length;
        return `${count} box${count === 1 ? "" : "es"}`;
    }
    if (kind === "networkInterfaces") {
        const count = Object.keys(items).length;
        return `${count} interface${count === 1 ? "" : "s"}`;
    }
    if (kind === "services") {
        const count = Object.keys(items).length;
        return `${count} service${count === 1 ? "" : "s"}`;
    }

    const count = Object.keys(items).length;
    return kind === "biometricDevices"
        ? `${count} device${count === 1 ? "" : "s"}`
        : `${count} setting${count === 1 ? "" : "s"}`;
}

const useStyles = makeStyles(() => ({
    sectionContainer: {
        marginTop: "8px",
    },
    acc: {
        border: `1px solid ${tokens.line}`,
        borderRadius: "12px",
        overflow: "hidden",
        background: tokens.surface,
        boxShadow: tokens.shadow,
    },
    panel: {
        boxShadow: "none",
        margin: "0 !important",
        "&:before": {
            display: "none",
        },
        "&:not(:first-child)": {
            borderTop: `1px solid ${tokens.line}`,
        },
    },
    summaryContent: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        paddingRight: "16px",
    },
    summaryTitles: {
        display: "flex",
        flexDirection: "column",
    },
    meta: {
        color: tokens.hint,
        fontSize: "12px",
    },
    title: {
        fontSize: "14px",
        fontWeight: 500,
    },
    details: {
        flexDirection: "column",
        background: tokens.canvas,
    },
    viewToggleRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "12px",
    },
    viewToggleGroup: {
        display: "inline-flex",
        border: `1px solid ${tokens.line}`,
        borderRadius: "7px",
        overflow: "hidden",
    },
    viewToggleBtn: {
        border: "none",
        background: tokens.surface,
        color: tokens.hint,
        fontSize: "11.5px",
        fontWeight: 600,
        padding: "5px 12px",
        cursor: "pointer",
        fontFamily: "inherit",
        "&:first-child": {
            borderRight: `1px solid ${tokens.line}`,
        },
    },
    viewToggleBtnActive: {
        background: tokens.accentSoft,
        color: tokens.accent,
    },
    biometricDeviceRow: {
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: "12px",
        marginBottom: "16px",
    },
    addRow: {
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "12px",
    },
    inlineHeading: {
        display: "flex",
        alignItems: "center",
        gap: "4px",
    },
}));

interface Props {
    summary: SiteCommissioningSummary;
    siteId?: number;
    canEdit?: boolean;
    onSummaryUpdated?: (summary: SiteCommissioningSummary) => void;
}

export const CommissioningSection: React.FC<Props> = ({ summary, siteId, canEdit = false, onSummaryUpdated }) => {
    const classes = useStyles();
    const { siteApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { showConfirmDialog } = useConfirmDialog();
    const fingerprintSection = summary.sections.find((s) => s.name === "fingerprintConfiguration");
    const fingerprintFields = fingerprintSection?.items?.config?.fields ?? {};
    const hasFingerprintFields = Object.keys(fingerprintFields).length > 0;
    const ioDevicesSection = summary.sections.find((s) => s.name === "ioDevices");
    const boxesSection = summary.sections.find((s) => s.name === "boxes");

    const displayRows: DisplayRow[] = [];
    summary.sections.forEach((s) => {
        if (s.name === "fingerprintConfiguration") {
            return;
        }
        if (s.name === "ioDevices") {
            if (Object.keys(s.items).length > 0 || canEdit) {
                displayRows.push({ key: "ioDevices", label: "I/O Devices", kind: "ioDevices", dataSection: s, items: s.items });
            }
            return;
        }
        if (s.name === "boxes") {
            if (Object.keys(s.items).length > 0 || canEdit) {
                displayRows.push({ key: "boxes", label: "Boxes", kind: "boxes", dataSection: s, items: s.items });
            }
            return;
        }
        if (s.name === "biometricDevices") {
            if (Object.keys(s.items).length > 0 || canEdit || hasFingerprintFields) {
                displayRows.push({ key: "biometricDevices", label: "Biometric Device", kind: "biometricDevices", dataSection: s, items: s.items });
            }
            return;
        }
        if (s.name === "networkInterfaces") {
            if (Object.keys(s.items).length > 0) {
                displayRows.push({ key: "networkInterfaces", label: "Network Interfaces", kind: "networkInterfaces", dataSection: s, items: s.items });
            }
            return;
        }
        if (s.name === "services") {
            if (Object.keys(s.items).length > 0) {
                displayRows.push({ key: "services", label: "Services", kind: "services", dataSection: s, items: s.items });
            }
            return;
        }
        if (Object.keys(s.items).length > 0) {
            displayRows.push({
                key: s.name,
                label: SECTION_LABELS[s.name] ?? humanizeSectionName(s.name),
                kind: s.name === "hmiConfiguration" || s.name === "systemConfiguration" ? "singleton" : "generic",
                dataSection: s,
                items: s.items,
            });
        }
    });
    const [expanded, setExpanded] = useState<Record<string, boolean>>({});
    const [jsonView, setJsonView] = useState<Record<string, boolean>>({});
    const [editingSingleton, setEditingSingleton] = useState<EditableSingletonSection | null>(null);
    const [biometricDialogOpen, setBiometricDialogOpen] = useState(false);
    const [editingBiometricSerial, setEditingBiometricSerial] = useState<string | undefined>(undefined);
    useForceRerender(30000);

    const findSection = (name: string) => summary.sections.find((s) => s.name === name);
    const biometricDeviceKeys = Object.keys(findSection("biometricDevices")?.items ?? {});
    const hasBiometricDevice = biometricDeviceKeys.length > 0;

    const removeBiometricDevice = (serialNumber: string) => {
        showConfirmDialog(`Remove biometric device ${serialNumber}?`).then((confirmed) => {
            if (!confirmed || !siteId) {
                return;
            }
            const items = { ...(findSection("biometricDevices")?.items ?? {}) };
            delete items[serialNumber];

            siteApi.updateCommissioning(siteId, [{ name: "biometricDevices", items }]).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    onSummaryUpdated?.(r.data);
                    enqueueSnackbar("Biometric device removed", { variant: "success" });
                } else {
                    enqueueSnackbar("ERROR: Failed to remove the biometric device", { variant: "error" });
                }
            });
        });
    };

    return (
        <div className={classes.sectionContainer}>
            <ZoneHead
                icon={<DevicesOtherOutlinedIcon style={{ fontSize: "16px" }} />}
                label="Site Configuration"
                asOf={
                    displayRows.length > 0 && summary.statusReportedAt
                        ? `Synced ${formatRelativeTime(summary.statusReportedAt)} · document rev ${summary.documentVersion}`
                        : undefined
                }
            />

            {displayRows.length === 0 && (
                <Typography variant="body2">This site has not been commissioned yet.</Typography>
            )}

            {displayRows.length > 0 && (
                <div className={classes.acc}>
                    {displayRows.map((row) => {
                        return (
                            <ExpansionPanel
                                key={row.key}
                                className={classes.panel}
                                expanded={expanded[row.key] ?? false}
                                onChange={(_, isExpanded) => setExpanded((prev) => ({ ...prev, [row.key]: isExpanded }))}
                            >
                                <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
                                    <div className={classes.summaryContent}>
                                        <div className={classes.summaryTitles}>
                                            <Typography component="b" className={classes.title}>
                                                {row.label}
                                            </Typography>
                                            <Typography component="span" className={classes.meta}>
                                                {summarizeRow(row.kind, row.items)} · Last edited{" "}
                                                {formatRelativeTime(
                                                    row.kind === "biometricDevices"
                                                        ? latestTimestamp(row.dataSection.lastEditedAt, fingerprintSection?.lastEditedAt)
                                                        : row.dataSection.lastEditedAt
                                                )}
                                            </Typography>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <SectionStatusChip
                                                status={
                                                    row.kind === "biometricDevices"
                                                        ? worseStatus(row.dataSection.status, fingerprintSection?.status)
                                                        : row.dataSection.status
                                                }
                                                reason={row.dataSection.statusReason}
                                            />
                                        </div>
                                    </div>
                                </ExpansionPanelSummary>
                                <ExpansionPanelDetails className={classes.details}>
                                    <div className={classes.viewToggleRow}>
                                        {canEdit && row.kind === "singleton" && siteId ? (
                                            <IconButton
                                                size="small"
                                                onClick={() => setEditingSingleton(row.dataSection.name as EditableSingletonSection)}
                                                title="Edit"
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        ) : (
                                            <span />
                                        )}
                                        <div className={classes.viewToggleGroup}>
                                            <button
                                                type="button"
                                                className={`${classes.viewToggleBtn} ${!jsonView[row.key] ? classes.viewToggleBtnActive : ""}`}
                                                onClick={() => setJsonView((prev) => ({ ...prev, [row.key]: false }))}
                                            >
                                                Fields
                                            </button>
                                            <button
                                                type="button"
                                                className={`${classes.viewToggleBtn} ${jsonView[row.key] ? classes.viewToggleBtnActive : ""}`}
                                                onClick={() => setJsonView((prev) => ({ ...prev, [row.key]: true }))}
                                            >
                                                JSON
                                            </button>
                                        </div>
                                    </div>

                                    {jsonView[row.key] ? (
                                        <SectionJsonView
                                            data={
                                                row.kind === "biometricDevices"
                                                    ? { scannerConfiguration: fingerprintFields, devices: row.dataSection.items }
                                                    : row.items
                                            }
                                        />
                                    ) : row.kind === "boxes" || row.kind === "ioDevices" ? (
                                        <SiteTopologyView
                                            items={row.dataSection.items}
                                            otherItems={
                                                (row.kind === "boxes" ? ioDevicesSection : boxesSection)?.items ?? {}
                                            }
                                            scope={row.kind}
                                            sectionStatus={row.dataSection.status}
                                            siteId={siteId}
                                            canEdit={canEdit}
                                            onSummaryUpdated={onSummaryUpdated}
                                        />
                                    ) : row.kind === "networkInterfaces" ? (
                                        <NetworkInterfacesView items={row.dataSection.items} />
                                    ) : row.kind === "services" ? (
                                        <ServicesView items={row.dataSection.items} />
                                    ) : row.kind === "biometricDevices" ? (
                                        <>
                                            {canEdit && siteId && (
                                                <div className={classes.addRow}>
                                                    <Button
                                                        size="small"
                                                        onClick={() => {
                                                            setEditingBiometricSerial(undefined);
                                                            setBiometricDialogOpen(true);
                                                        }}
                                                        disabled={hasBiometricDevice}
                                                        title={hasBiometricDevice ? "Only one biometric device is allowed per site" : "Add biometric device"}
                                                    >
                                                        <AddIcon fontSize="small" style={{ marginRight: "4px" }} />
                                                        Add
                                                    </Button>
                                                </div>
                                            )}
                                            {(hasFingerprintFields || (canEdit && siteId)) && (
                                                <div style={{ marginBottom: "16px" }}>
                                                    <div className={classes.inlineHeading}>
                                                        <Typography variant="subtitle2">Scanner Configuration</Typography>
                                                        {canEdit && siteId && (
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => setEditingSingleton("fingerprintConfiguration")}
                                                                title="Edit scanner configuration"
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </div>
                                                    {hasFingerprintFields && <DynamicFieldsList fields={fingerprintFields} />}
                                                </div>
                                            )}
                                            {Object.keys(row.items).length > 0 && (
                                                <Typography variant="subtitle2">Registered Devices</Typography>
                                            )}
                                            {Object.entries(row.items).map(([key, item]) => {
                                                const { periodicSyncEnabled: _periodicSyncEnabled, ...displayFields } = item.fields;
                                                return (
                                                <div key={key} className={classes.biometricDeviceRow}>
                                                    <div style={{ flexGrow: 1 }}>
                                                        <Typography variant="subtitle2">{key}</Typography>
                                                        <DynamicFieldsList fields={displayFields} />
                                                    </div>
                                                    {canEdit && siteId && (
                                                        <div style={{ display: "flex", gap: "4px", flexShrink: 0 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => {
                                                                    setEditingBiometricSerial(key);
                                                                    setBiometricDialogOpen(true);
                                                                }}
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                            <IconButton size="small" onClick={() => removeBiometricDevice(key)}>
                                                                <DeleteOutlineIcon fontSize="small" />
                                                            </IconButton>
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        <DynamicFieldsList fields={Object.values(row.items)[0]?.fields ?? {}} />
                                    )}
                                </ExpansionPanelDetails>
                            </ExpansionPanel>
                        );
                    })}
                </div>
            )}

            {siteId && (
                <>
                    <EditHmiConfigurationDialog
                        open={editingSingleton === "hmiConfiguration"}
                        onClose={() => setEditingSingleton(null)}
                        siteId={siteId}
                        fields={findSection("hmiConfiguration")?.items?.config?.fields ?? {}}
                        onSaved={(s) => onSummaryUpdated?.(s)}
                    />
                    <EditFingerprintConfigurationDialog
                        open={editingSingleton === "fingerprintConfiguration"}
                        onClose={() => setEditingSingleton(null)}
                        siteId={siteId}
                        fields={findSection("fingerprintConfiguration")?.items?.config?.fields ?? {}}
                        onSaved={(s) => onSummaryUpdated?.(s)}
                    />
                    <EditSystemConfigurationDialog
                        open={editingSingleton === "systemConfiguration"}
                        onClose={() => setEditingSingleton(null)}
                        siteId={siteId}
                        fields={findSection("systemConfiguration")?.items?.config?.fields ?? {}}
                        onSaved={(s) => onSummaryUpdated?.(s)}
                    />
                    <EditBiometricDeviceDialog
                        open={biometricDialogOpen}
                        onClose={() => setBiometricDialogOpen(false)}
                        siteId={siteId}
                        existingItems={findSection("biometricDevices")?.items ?? {}}
                        editingSerialNumber={editingBiometricSerial}
                        onSaved={(s) => onSummaryUpdated?.(s)}
                    />
                </>
            )}
        </div>
    );
};
